import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUserSlash,
    faEllipsisH,
    faPrint,
    faEye,
    faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';

import { NavLink } from "react-router-dom";

import DateCell from "../Listing/DateCell";
import fitColumns from "../Listing/fitColumns";

import PatientImage from "../Patient/PatientImage";
import PaymentMethodBadge from './PaymentMethodBadge';

import InputText from "../Form/Fields/InputText";
import InputSliderRange from "../Form/Fields/InputSliderRange";
import InputPatient from "../Form/Fields/InputPatient";
import { TotalCell } from "./orderColumns";

export const formatAmount = (val) =>
    new Intl.NumberFormat("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

// Default view: # · Paiement · Montant · Méthode · Date · Patient · Reçu / Rendu · Facture
const buildPaymentColumns = ({ handleViewPdf, downloadInvoice, amountMax = 1000 }) => [
    {
        Header: "#",
        accessor: "id",
        sortable: true,
        className: 'text-center',
        filterable: { component: InputText },
        Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: "#9ca3af" }}>{row.id}</span>,
    },
    {
        Header: "Paiement",
        accessor: "paymentNumber",
        filterable: { component: InputText },
        Cell: (row) => (
            <div>
                {/* same reference style as the orders listing (orderColumns.js) */}
                <NavLink to={`/payments/${row.id}`} className="cell-name" style={{ whiteSpace: 'nowrap', fontSize: 13.5 }}>{row.paymentNumber}</NavLink>
            </div>
        ),
    },
    {
        Header: "Montant",
        accessor: "amount",
        activeColumn: true,
        filterable: { component: InputSliderRange, min: 0, max: amountMax, step: 1, unit: "dhs" },
        sortable: true,
        className: "text-end",
        // same amount cell as the invoices / sales listings (TotalCell), right-aligned
        Cell: (row) => (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <TotalCell total={row.amount} />
            </div>
        ),
    },
    {
        Header: "Méthode",
        accessor: "paymentMethod",
        filterable: { component: InputText },
        Cell: (row) => row.paymentMethod
            ? <PaymentMethodBadge method={row.paymentMethod} />
            : <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>,
    },
    {
        // exact date + time, relative age underneath (same as the sales listing)
        Header: "Date",
        accessor: "paymentCreatedAt",
        Cell: (row) => <DateCell date={row.paymentCreatedAt} />,
    },
    {
        Header: "Patient",
        accessor: "patientId",
        filterable: { component: InputPatient },
        Cell: (row) => {
            if (row.patient != null) {
                return (
                    <div className="d-flex align-items-center gap-2">
                        <PatientImage patient={row.patient} size={32} />
                        <div>
                            <NavLink className="cell-name" style={{ fontSize: 13 }} to={`/patient/${row.patient.id}`}>
                                {row.patient.firstName}{row.patient.lastName ? ` ${row.patient.lastName[0]}.` : ''}
                            </NavLink>
                        </div>
                    </div>
                );
            }
            return (
                <div className="d-flex align-items-center gap-2">
                    <span style={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <FontAwesomeIcon icon={faUserSlash} style={{ color: "#9ca3af", fontSize: 13 }} />
                    </span>
                    <span className="cell-sub" style={{ marginTop: 0 }}>Visiteur</span>
                </div>
            );
        },
    },
    {
        Header: "Reçu / Rendu",
        accessor: "cashGiven",
        Cell: (row) => {
            if (row.paymentMethod !== 'CASH' || row.cashGiven == null) {
                return <span style={{ color: '#d1d5db' }}>—</span>;
            }
            const change = (parseFloat(row.cashGiven) - parseFloat(row.amount)).toFixed(2);
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                        {formatAmount(row.cashGiven)} <span style={{ fontSize: 11, color: '#9ca3af' }}>dhs</span>
                    </span>
                    <span style={{ fontSize: 11, color: parseFloat(change) >= 0 ? '#059669' : '#dc2626', fontWeight: 500 }}>
                        rendu {formatAmount(change)} dhs
                    </span>
                </div>
            );
        },
    },
    {
        Header: "Facture",
        accessor: "invoiceId",
        className: "text-center",
        Cell: (row) => row.invoiceId
            ? (
                <NavLink
                    to={`/invoices/${row.invoiceId}`}
                    style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: 8,
                        background: '#f3f4f6', color: '#6b7280',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#6b7280'; }}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={row.invoiceNumber}
                >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: 12 }} />
                </NavLink>
            )
            : <span style={{ color: '#d1d5db' }}>—</span>,
    },
    {
        Header: "",
        accessor: "actions",
        className: "col-actions",
        Cell: (row) => (
            <div className="cell-dropdown">
                <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                    <FontAwesomeIcon icon={faEllipsisH} />
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                        <NavLink className="dropdown-item" to={`/payments/${row.id}`}>
                            <FontAwesomeIcon icon={faEye} className="me-2 text-muted" />
                            Voir les détails
                        </NavLink>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={() => handleViewPdf?.(row.id)}>
                            <FontAwesomeIcon icon={faPrint} className="me-2 text-muted" />
                            Imprimer le reçu
                        </button>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                        <button className="dropdown-item danger">Annuler le paiement</button>
                    </li>
                </ul>
            </div>
        ),
    },
];

// only the payment number stretches, the others fit their content (fitColumns)
export const getPaymentColumns = ({ handleViewPdf, downloadInvoice, amountMax = 1000 }) => fitColumns(buildPaymentColumns({ handleViewPdf, downloadInvoice, amountMax }), "paymentNumber");
